import { Geocoder, hasValue, LocationTracker, log, PushWarningNotification } from "@repo/helpers";
import { Button, ButtonSize, ButtonTypes, Spinner } from "@repo/ui";
import { useEffect, useRef, useState } from "react";

import style from "./style.module.scss";
import { Props } from "./types";

const RESOLVE_FUNCTION_NAME = "initialLocationReceivedCallback";

export default function CurrentLocationFinder(props: Props) {
	const { propertyName, valueChanged } = props;

	const [buttonText, setButtonText] = useState<string | JSX.Element>("Find My Location");
	const [error, setError] = useState<string>(null);
	const [disabled, setDisabled] = useState(false);

	const locationTracker = useRef(props.locationTracker ?? new LocationTracker());

	useEffect(() => {
		if (hasValue(error)) {
			log(error, "error");
			PushWarningNotification(error);
		}
	}, [error]);

	useEffect(() => {
		locationTracker.current.addResolveFunction(RESOLVE_FUNCTION_NAME, resetFields);
		locationTracker.current.continueTracking();
	}, [locationTracker]);

	async function findCurrentLocation() {
		setDisabled(true);
		setButtonText(<Spinner>{"Locating..."}</Spinner>);

		async function success(results) {
			try {
				const addressComponents = results[0].address_components as any[];

				const streetNumber = addressComponents.find((ac) => ac.types.includes("street_number"))?.long_name;
				const route = addressComponents.find((ac) => ac.types.includes("route"))?.short_name;
				const zip = addressComponents.find((ac) => ac.types.includes("postal_code"))?.short_name;
				const address1 = `${streetNumber} ${route}`;

				if (!streetNumber || !route || !zip) {
					setError("Could not find current location.");
				} else {
					await valueChanged(address1, `${propertyName}.address1`, propertyName);
					await valueChanged(zip, `${propertyName}.zip`, propertyName);
					resetFields();
				}
			} catch {
				setError("Could not find current location.");
			} finally {
				setDisabled(false);
			}
		}

		function failure(error) {
			setError(error);
			setDisabled(false);
		}

		const currentLocation = locationTracker.current.mostRecentPosition();
		if (currentLocation && currentLocation.coords) {
			const { latitude, longitude } = currentLocation.coords;
			const { fromLatLng } = new Geocoder();
			fromLatLng(latitude, longitude, ["premise", "street_address"]).then(success, failure);
		} else {
			setError("Geolocation failed. Please try again.");
			setButtonText("Find My Location");
			setDisabled(false);
		}
	}

	function resetFields(coords?: GeolocationCoordinates) {
		setError(null);
		setDisabled(false);
		setButtonText("Find My Location");
	}

	return (
		<>
			<Button
				className={style.button}
				types={[ButtonTypes.Primary]}
				size={ButtonSize.large}
				fullWidth
				disabled={disabled}
				onClick={findCurrentLocation}
			>
				{buttonText}
			</Button>
			{error && <h6 className={style.error}>{error}</h6>}
		</>
	);
}
